import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  AlertTriangle,
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  History,
  Loader2,
  Save,
  Settings2,
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

import { EntityActivityPanel } from '@/components/activity/EntityActivityPanel';
import { ConfigurationForm } from '@/components/configurations/ConfigurationForm';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import {
  useConfiguration,
  useCreateConfiguration,
  useUpdateConfiguration,
} from '@/hooks/useConfigurations';
import {
  useImprovementSettings,
  useUpdateImprovementSettings,
} from '@/hooks/useImprovementSettings';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useWorkflows } from '@/hooks/useWorkflows';
import { extractErrorMessage, extractValidationErrors } from '@/lib/utils';
import { RepositorySection } from '@/pages/configurations/RepositorySection';
import { ScenariosSection } from '@/pages/configurations/ScenariosSection';
import { useAuthStore } from '@/stores/auth.store';
import { useFeaturesStore } from '@/stores/features.store';

import type { ImprovementSettings } from '@/lib/api/pipeline-runs.api';
import type { CreateConfigurationSetupDto, UpdateConfigurationSetupDto } from '@repo/shared-types';

// ── Allowed areas options ────────────────────────────────────────────────────

const AREA_OPTIONS = [
  { value: 'prompt_section', label: 'Prompt sections' },
  { value: 'prompt_rules', label: 'Prompt rules' },
  { value: 'tool_description', label: 'Tool descriptions' },
  { value: 'advanced', label: 'Advanced settings' },
  { value: 'agent_node', label: 'Agent nodes' },
  { value: 'agent_edge', label: 'Agent edges' },
];

// ── Validation helpers ───────────────────────────────────────────────────────

interface ValidationErrors {
  schedule_cron?: string;
  max_iterations?: string;
  held_out_size?: string;
  max_prompt_growth_pct?: string;
  max_test_cases?: string;
  min_confirmed_findings?: string;
  promotion_cooldown_hours?: string;
  rollback_window_calls?: string;
  rollback_window_hours?: string;
  rollback_threshold_pct?: string;
}

const validate = (draft: Partial<ImprovementSettings>): ValidationErrors => {
  const errs: ValidationErrors = {};
  const cron = draft.schedule_cron ?? '';
  if (cron && cron.trim().split(/\s+/).length !== 5) {
    errs.schedule_cron = 'Cron must have exactly 5 fields (e.g. 0 6 * * *)';
  }
  const mi = draft.max_iterations;
  if (mi !== undefined && (mi < 1 || mi > 10)) {
    errs.max_iterations = 'Must be 1-10';
  }
  const hs = draft.held_out_size;
  if (hs !== undefined && (hs < 0 || hs > 20)) {
    errs.held_out_size = 'Must be 0-20';
  }
  const gp = draft.max_prompt_growth_pct;
  if (gp !== undefined && (gp < 1 || gp > 100)) {
    errs.max_prompt_growth_pct = 'Must be 1-100';
  }
  const mtc = draft.max_test_cases;
  if (mtc !== undefined && mtc !== null && mtc < 1) {
    errs.max_test_cases = 'Must be at least 1';
  }
  if (draft.autonomy_level === 'auto') {
    const mcf = draft.min_confirmed_findings;
    if (mcf !== undefined && mcf < 1) errs.min_confirmed_findings = 'Must be at least 1';
    const pch = draft.promotion_cooldown_hours;
    if (pch !== undefined && pch < 0) errs.promotion_cooldown_hours = 'Must be 0 or more';
    const rwc = draft.rollback_window_calls;
    if (rwc !== undefined && rwc < 1) errs.rollback_window_calls = 'Must be at least 1';
    const rwh = draft.rollback_window_hours;
    if (rwh !== undefined && rwh < 1) errs.rollback_window_hours = 'Must be at least 1';
    const rtp = draft.rollback_threshold_pct;
    if (rtp !== undefined && (rtp < 1 || rtp > 100)) errs.rollback_threshold_pct = 'Must be 1-100';
  }
  return errs;
};

// ── Dirty tracking helper ────────────────────────────────────────────────────

const settingsToComparable = (s: Partial<ImprovementSettings>): string =>
  JSON.stringify({
    autonomy_level: s.autonomy_level,
    schedule_cron: s.schedule_cron,
    timezone: s.timezone,
    max_iterations: s.max_iterations,
    held_out_size: s.held_out_size,
    run_baseline_replay: s.run_baseline_replay,
    max_test_cases: s.max_test_cases,
    max_prompt_growth_pct: s.max_prompt_growth_pct,
    allowed_areas: [...(s.allowed_areas ?? [])].sort(),
    report_workflow_id: s.report_workflow_id,
    verifier_model: s.verifier_model,
    patch_model: s.patch_model,
    simulator_model: s.simulator_model,
    min_confirmed_findings: s.min_confirmed_findings,
    promotion_cooldown_hours: s.promotion_cooldown_hours,
    rollback_window_calls: s.rollback_window_calls,
    rollback_window_hours: s.rollback_window_hours,
    rollback_threshold_pct: s.rollback_threshold_pct,
  });

// ── Inline error message ─────────────────────────────────────────────────────

const FieldError = ({ message }: { message?: string }) =>
  message ? <p className="text-xs text-red-600 dark:text-red-400">{message}</p> : null;

// ── ImprovementSettingsSection ───────────────────────────────────────────────

const ImprovementSettingsSection = ({ configId }: { configId: string }) => {
  const { data: settings, isLoading } = useImprovementSettings(configId);
  const updateSettings = useUpdateImprovementSettings(configId);
  const { data: workflows } = useWorkflows();
  const { toast } = useToast();

  const [draft, setDraft] = useState<Partial<ImprovementSettings>>({});
  const [baseline, setBaseline] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Seed draft and baseline when settings arrive
  useEffect(() => {
    if (settings) {
      const seed: Partial<ImprovementSettings> = {
        autonomy_level: settings.autonomy_level,
        schedule_cron: settings.schedule_cron,
        timezone: settings.timezone,
        max_iterations: settings.max_iterations,
        held_out_size: settings.held_out_size,
        run_baseline_replay: settings.run_baseline_replay,
        max_test_cases: settings.max_test_cases,
        max_prompt_growth_pct: settings.max_prompt_growth_pct,
        allowed_areas: settings.allowed_areas,
        report_workflow_id: settings.report_workflow_id,
        verifier_model: settings.verifier_model,
        patch_model: settings.patch_model,
        simulator_model: settings.simulator_model,
        min_confirmed_findings: settings.min_confirmed_findings,
        promotion_cooldown_hours: settings.promotion_cooldown_hours,
        rollback_window_calls: settings.rollback_window_calls,
        rollback_window_hours: settings.rollback_window_hours,
        rollback_threshold_pct: settings.rollback_threshold_pct,
      };
      setDraft(seed);
      setBaseline(settingsToComparable(seed));
    }
  }, [settings]);

  const validationErrors = useMemo(() => validate(draft), [draft]);
  const hasErrors = Object.keys(validationErrors).length > 0;
  const isDirty = baseline !== '' && settingsToComparable(draft) !== baseline;

  const handleSave = useCallback(async () => {
    if (hasErrors) return;
    try {
      setIsSaving(true);
      await updateSettings.mutateAsync(draft);
      setBaseline(settingsToComparable(draft));
      toast({
        variant: 'success',
        title: 'Pipeline settings saved',
        description: 'Improvement pipeline settings have been updated.',
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Failed to save settings',
        description: extractErrorMessage(error),
      });
    } finally {
      setIsSaving(false);
    }
  }, [draft, hasErrors, toast, updateSettings]);

  const updateField = <K extends keyof ImprovementSettings>(
    key: K,
    value: ImprovementSettings[K],
  ) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  const toggleArea = (area: string, checked: boolean) => {
    const current = (draft.allowed_areas as string[]) ?? [];
    const next = checked ? [...current, area] : current.filter((a) => a !== area);
    updateField('allowed_areas', next);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="text-muted-foreground h-5 w-5 animate-spin" />
      </div>
    );
  }

  const isAuto = draft.autonomy_level === 'auto';

  return (
    <div className="space-y-6 px-4 py-4">
      {/* Autonomy level */}
      <div className="space-y-2">
        <label className="text-sm font-medium">Autonomy level</label>
        <Select
          value={draft.autonomy_level ?? 'off'}
          onValueChange={(v) =>
            updateField('autonomy_level', v as ImprovementSettings['autonomy_level'])
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="off">Off</SelectItem>
            <SelectItem value="propose">Propose</SelectItem>
            <SelectItem value="auto">Auto</SelectItem>
          </SelectContent>
        </Select>
        {isAuto && (
          <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 dark:border-amber-700 dark:bg-amber-950/30">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
            <p className="text-xs text-amber-800 dark:text-amber-300">
              AUTO mode will promote changes without manual review. Guardrail checks apply but
              ensure you trust the pipeline configuration before enabling.
            </p>
          </div>
        )}
      </div>

      {/* Schedule */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className="text-sm font-medium">Schedule (cron)</label>
          <Input
            value={draft.schedule_cron ?? ''}
            onChange={(e) => updateField('schedule_cron', e.target.value)}
            placeholder="0 6 * * *"
          />
          <FieldError message={validationErrors.schedule_cron} />
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium">Timezone</label>
          <Input
            value={draft.timezone ?? ''}
            onChange={(e) => updateField('timezone', e.target.value)}
            placeholder="Europe/London"
          />
        </div>
      </div>

      {/* Iteration limits */}
      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-2">
          <label className="text-sm font-medium">Max iterations</label>
          <Input
            type="number"
            min={1}
            max={10}
            value={draft.max_iterations ?? 3}
            onChange={(e) => updateField('max_iterations', Number(e.target.value))}
          />
          <FieldError message={validationErrors.max_iterations} />
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium">Held-out size</label>
          <Input
            type="number"
            min={0}
            max={20}
            value={draft.held_out_size ?? 3}
            onChange={(e) => updateField('held_out_size', Number(e.target.value))}
          />
          <FieldError message={validationErrors.held_out_size} />
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium">Max test cases</label>
          <Input
            type="number"
            min={1}
            value={draft.max_test_cases ?? ''}
            onChange={(e) =>
              updateField('max_test_cases', e.target.value ? Number(e.target.value) : null)
            }
            placeholder="Unlimited"
          />
          <FieldError message={validationErrors.max_test_cases} />
        </div>
      </div>

      {/* Growth cap & baseline */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className="text-sm font-medium">Max prompt growth (%)</label>
          <Input
            type="number"
            min={1}
            max={100}
            value={draft.max_prompt_growth_pct ?? 15}
            onChange={(e) => updateField('max_prompt_growth_pct', Number(e.target.value))}
          />
          <FieldError message={validationErrors.max_prompt_growth_pct} />
        </div>
        <div className="flex items-end pb-2">
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={draft.run_baseline_replay ?? true}
              onCheckedChange={(checked) => updateField('run_baseline_replay', !!checked)}
            />
            Run baseline replay
          </label>
        </div>
      </div>

      {/* Allowed areas */}
      <div className="space-y-2">
        <label className="text-sm font-medium">Allowed areas</label>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {AREA_OPTIONS.map((opt) => (
            <label key={opt.value} className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={(draft.allowed_areas ?? []).includes(opt.value)}
                onCheckedChange={(checked) => toggleArea(opt.value, !!checked)}
              />
              {opt.label}
            </label>
          ))}
        </div>
      </div>

      {/* Models */}
      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-2">
          <label className="text-sm font-medium">Verifier model</label>
          <Input
            value={draft.verifier_model ?? ''}
            onChange={(e) => updateField('verifier_model', e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium">Patch model</label>
          <Input
            value={draft.patch_model ?? ''}
            onChange={(e) => updateField('patch_model', e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium">Simulator model</label>
          <Input
            value={draft.simulator_model ?? ''}
            onChange={(e) => updateField('simulator_model', e.target.value)}
          />
        </div>
      </div>

      {/* Report workflow picker */}
      <div className="space-y-2">
        <label className="text-sm font-medium">Report workflow</label>
        <Select
          value={draft.report_workflow_id ?? '__none__'}
          onValueChange={(v) => updateField('report_workflow_id', v === '__none__' ? null : v)}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select a workflow..." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">None</SelectItem>
            {workflows?.map((wf) => (
              <SelectItem key={wf.id} value={wf.id}>
                {wf.name}{' '}
                <span className="text-muted-foreground text-xs">({wf.id.slice(0, 8)})</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-muted-foreground text-xs">
          Workflow used to deliver improvement reports (email, Slack, etc.)
        </p>
      </div>

      {/* AUTO guardrails -- shown only when autonomy is auto */}
      {isAuto && (
        <div className="space-y-4 rounded-md border p-4 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs">
              AUTO guardrails
            </Badge>
          </div>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <div className="space-y-2">
              <label className="text-sm font-medium">Min confirmed findings</label>
              <Input
                type="number"
                min={1}
                value={draft.min_confirmed_findings ?? 1}
                onChange={(e) => updateField('min_confirmed_findings', Number(e.target.value))}
              />
              <FieldError message={validationErrors.min_confirmed_findings} />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Promotion cooldown (hours)</label>
              <Input
                type="number"
                min={0}
                value={draft.promotion_cooldown_hours ?? 24}
                onChange={(e) => updateField('promotion_cooldown_hours', Number(e.target.value))}
              />
              <FieldError message={validationErrors.promotion_cooldown_hours} />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Rollback window (calls)</label>
              <Input
                type="number"
                min={1}
                value={draft.rollback_window_calls ?? 50}
                onChange={(e) => updateField('rollback_window_calls', Number(e.target.value))}
              />
              <FieldError message={validationErrors.rollback_window_calls} />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Rollback window (hours)</label>
              <Input
                type="number"
                min={1}
                value={draft.rollback_window_hours ?? 48}
                onChange={(e) => updateField('rollback_window_hours', Number(e.target.value))}
              />
              <FieldError message={validationErrors.rollback_window_hours} />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Rollback threshold (%)</label>
              <Input
                type="number"
                min={1}
                max={100}
                value={draft.rollback_threshold_pct ?? 20}
                onChange={(e) => updateField('rollback_threshold_pct', Number(e.target.value))}
              />
              <p className="text-muted-foreground text-xs">
                Relative increase in failure rate that triggers auto-rollback (e.g. 20 = roll back
                if rate rises by more than 20% relative to baseline, with a minimum 5 pp absolute
                increase).
              </p>
              <FieldError message={validationErrors.rollback_threshold_pct} />
            </div>
          </div>
        </div>
      )}

      {/* Save */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={isSaving || !isDirty || hasErrors} size="sm">
          {isSaving ? (
            <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
          ) : (
            <Save className="mr-1.5 h-4 w-4" />
          )}
          Save pipeline settings
        </Button>
      </div>
    </div>
  );
};

// ── ConfigurationEditPage ────────────────────────────────────────────────────

export const ConfigurationEditPage = () => {
  const { id = '' } = useParams();
  const isNew = id === 'new';
  const navigate = useNavigate();
  const { toast } = useToast();

  const { data: configuration, isLoading } = useConfiguration(isNew ? '' : id);
  const createMutation = useCreateConfiguration();
  const updateMutation = useUpdateConfiguration();

  const [errors, setErrors] = useState<Array<{ field: string; message: string }>>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showPipelineSettings, setShowPipelineSettings] = useState(false);

  // Rendered below the form rather than as a tab so opening the history never
  // unmounts the form and loses unsaved edits.
  const isAdmin = useAuthStore((state) => state.user?.role === 'admin');
  const featureAgentImprovement = useFeaturesStore((s) => s.agentImprovement);

  usePageTitle(isNew ? 'New configuration' : configuration?.name || 'Configuration');

  const handleSubmit = async (values: UpdateConfigurationSetupDto) => {
    try {
      setErrors([]);
      setIsSaving(true);

      if (isNew) {
        await createMutation.mutateAsync(values as CreateConfigurationSetupDto);
        toast({
          variant: 'success',
          title: 'Configuration created',
          description: `"${values.name}" has been created successfully.`,
        });
      } else {
        await updateMutation.mutateAsync({ id, data: values });
        toast({
          variant: 'success',
          title: 'Configuration updated',
          description: `"${values.name}" has been updated successfully.`,
        });
      }
      navigate('/configurations');
    } catch (error) {
      const validationErrors = extractValidationErrors(error);
      if (validationErrors.length > 0) {
        setErrors(validationErrors);
        toast({
          variant: 'destructive',
          title: 'Please fix the highlighted fields',
          description: 'Some fields need attention before saving.',
        });
      } else {
        toast({
          variant: 'destructive',
          title: isNew ? 'Failed to create configuration' : 'Failed to update configuration',
          description: extractErrorMessage(error),
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  if (!isNew && isLoading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="text-center">
          <div className="border-primary mx-auto h-12 w-12 animate-spin rounded-full border-b-2" />
          <p className="text-muted-foreground mt-4">Loading configuration…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9"
            onClick={() => navigate('/configurations')}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">
              {isNew ? 'New configuration' : configuration?.name || 'Configuration'}
            </h1>
            <p className="text-muted-foreground text-sm">
              {isNew
                ? 'Define a new hybrid AI call configuration.'
                : 'Manage prompt, tools, knowledge base, and call settings.'}
            </p>
          </div>
        </div>
        <Button type="submit" form="configuration-form" disabled={isSaving}>
          <Save className="mr-1.5 h-4 w-4" />
          {isNew ? 'Create configuration' : 'Save changes'}
        </Button>
      </header>

      <ConfigurationForm
        initialValues={configuration ?? undefined}
        onSubmit={handleSubmit}
        errors={errors}
        isLoading={isSaving}
        submitLabel={isNew ? 'Create configuration' : 'Save changes'}
      />

      {!isNew && featureAgentImprovement && (
        <section className="rounded-lg border dark:border-gray-700">
          <button
            type="button"
            onClick={() => setShowPipelineSettings((prev) => !prev)}
            className="flex w-full items-center gap-2 px-4 py-3 text-left"
          >
            {showPipelineSettings ? (
              <ChevronDown className="text-muted-foreground h-4 w-4" />
            ) : (
              <ChevronRight className="text-muted-foreground h-4 w-4" />
            )}
            <Settings2 className="h-4 w-4" />
            <span className="text-sm font-medium">Pipeline settings</span>
            <span className="text-muted-foreground text-xs">
              Autonomy, schedule, models, and guardrails for the improvement pipeline
            </span>
          </button>
          {showPipelineSettings && (
            <div className="border-t dark:border-gray-700">
              <ImprovementSettingsSection configId={id} />
            </div>
          )}
        </section>
      )}

      {!isNew && featureAgentImprovement && <ScenariosSection configurationId={id} />}

      {!isNew && isAdmin && (
        <section className="rounded-lg border dark:border-gray-700">
          <button
            type="button"
            onClick={() => setShowHistory((prev) => !prev)}
            className="flex w-full items-center gap-2 px-4 py-3 text-left"
          >
            {showHistory ? (
              <ChevronDown className="text-muted-foreground h-4 w-4" />
            ) : (
              <ChevronRight className="text-muted-foreground h-4 w-4" />
            )}
            <History className="h-4 w-4" />
            <span className="text-sm font-medium">Change history</span>
            <span className="text-muted-foreground text-xs">
              Who changed this configuration, and what the values were before
            </span>
          </button>
          {showHistory && (
            <div className="border-t px-4 py-3 dark:border-gray-700">
              <EntityActivityPanel entityType="configuration" entityId={id} />
            </div>
          )}
        </section>
      )}

      {!isNew && isAdmin && featureAgentImprovement && <RepositorySection configurationId={id} />}
    </div>
  );
};
